import {customCheckboxStyles} from "./styles/custom-checkbox-styles";
import {customPluginAttrPrefix, customPluginNameAttr} from "@/components/tinyMce/plugins/tinyMceCustomPluginsConstants";

const t: any = {
  "fr": {
    "toolbarTooltip": 'Insérer une checklist',
  },
  "en": {
    "toolbarTooltip": 'Insert checklist',
  },
  "de": {
    "toolbarTooltip": 'Checkliste einfügen',
  },
  "es": {
    "toolbarTooltip": 'Insertar checklist',
  }
}

export const customCheckboxPluginName = 'customchecklist';

const customCheckboxContainerAttr = `${customPluginAttrPrefix}-${customCheckboxPluginName}-container`;
const customCheckboxItemAttr = `${customPluginAttrPrefix}-${customCheckboxPluginName}-item`;
const customCheckboxAttr = `${customPluginAttrPrefix}-${customCheckboxPluginName}`;
const customCheckboxTextAttr = `${customPluginAttrPrefix}-${customCheckboxPluginName}-text`;
const customCheckboxCheckedAttr = `${customPluginAttrPrefix}-${customCheckboxPluginName}-check`;

const generateId = () => `checkbox-${crypto.randomUUID()}`;
const createCheckboxItem = (onCheckedColor: string) => {
  const id = generateId();
  return `<div ${customPluginNameAttr}="${customCheckboxPluginName}" ${customCheckboxItemAttr}="${id}" class="tmce-plugin-custom-checkbox-block" ${customCheckboxCheckedAttr}="false">
              &emsp;<span ${customCheckboxAttr}="${id}" class="tmce-plugin-custom-checkbox" contenteditable="false"></span>
              <span ${customCheckboxTextAttr}="${id}">&nbsp;</span>
            </div>`;
};

export const useCustomCheckbox = (editor: any) => {
  const lang = editor.getParam('customchecklist_i18n_locale', 'en').toLowerCase();
  const onCheckedColor = editor.getParam('customchecklist_onCheckedColor', '#555555');

  editor.ui.registry.addButton(customCheckboxPluginName, {
    icon: 'checklist',
    tooltip: t[lang].toolbarTooltip,
    onAction: () => {
      const containerId = generateId();
      const containerHtml = `<div ${customCheckboxContainerAttr}="${containerId}" class="tmce-plugin-custom-checkbox-container">${createCheckboxItem(onCheckedColor)}</div>`;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = containerHtml;
      const container = tempDiv.firstChild as HTMLElement;

      editor.selection.getRng().insertNode(container);

      setTimeout(() => {
        const containers = editor.getBody().querySelectorAll(`[${customCheckboxContainerAttr}]`);
        const lastContainer = containers[containers.length - 1];
        const textSpan = lastContainer.firstChild?.querySelector(`[${customCheckboxTextAttr}]`);
        if (textSpan?.firstChild) {
          const range = editor.dom.createRng();
          range.setStart(textSpan.firstChild, textSpan.textContent?.length || 0);
          range.setEnd(textSpan.firstChild, textSpan.textContent?.length || 0);
          editor.selection.setRng(range);
          textSpan.focus();
        }
      }, 50);
    }
  });

  editor.on('preinit', () => {
    injectStyleSheet(editor);
  });

  editor.on('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleOnEnterKeydown(e);
    }

    if (e.key === 'Backspace') {
      handleBackspaceKeydown(e);
    }
  });

  // Toggle checkbox visuel au clic
  editor.on('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.hasAttribute(customCheckboxAttr)) {
      const item = target.closest(`[${customCheckboxItemAttr}]`) as HTMLElement;
      if (item) {
        const isChecked = item.getAttribute(customCheckboxCheckedAttr) === 'true';
        item.setAttribute(customCheckboxCheckedAttr, (!isChecked).toString());

        // Mettre à jour le style visuel
        if (!isChecked) {
          target.style.backgroundColor = onCheckedColor;
          target.style.borderColor = onCheckedColor;
          target.innerHTML = '<span class="tmce-plugin-custom-checkbox-check"></span>';
        } else {
          target.style.backgroundColor = 'white';
          target.style.borderColor = '#222f3e';
          target.innerHTML = '';
        }
      }
    }
  });

  function handleOnEnterKeydown(e: KeyboardEvent) {
    const node = editor.selection.getNode();
    const textSpan = node.hasAttribute?.(customCheckboxTextAttr) ? node : editor.dom.getParent(node, `[${customCheckboxTextAttr}]`);
    const checklistItem = textSpan ? editor.dom.getParent(textSpan, `[${customCheckboxItemAttr}]`) : null;
    const container = checklistItem ? editor.dom.getParent(checklistItem, `[${customCheckboxContainerAttr}]`) : null;

    if (container && checklistItem && textSpan) {
      e.preventDefault();

      if (textSpan.textContent?.trim() === '') {
        editor.dom.remove(checklistItem);
        const newP = editor.dom.create('p', {}, '<br>');
        editor.dom.insertAfter(newP, container);
        editor.selection.setCursorLocation(newP, 0);
        return;
      }

      const newItem = editor.dom.createFragment(createCheckboxItem(onCheckedColor)).firstChild;
      editor.dom.insertAfter(newItem, checklistItem);

      setTimeout(() => {
        const newSpan = newItem.querySelector(`[${customCheckboxTextAttr}]`);
        if (newSpan) {
          const range = editor.dom.createRng();
          range.setStart(newSpan.firstChild, newSpan.textContent?.length || 0);
          range.setEnd(newSpan.firstChild, newSpan.textContent?.length || 0);
          editor.selection.setRng(range);
          newSpan.focus();
        }
      }, 10);
    }
  }

  function handleBackspaceKeydown(e: KeyboardEvent) {
    const node = editor.selection.getNode();
    const textSpan = node.hasAttribute?.(customCheckboxTextAttr) ? node : editor.dom.getParent(node, `[${customCheckboxTextAttr}]`);
    const checklistItem = textSpan ? editor.dom.getParent(textSpan, `[${customCheckboxItemAttr}]`) : null;
    const container = checklistItem ? editor.dom.getParent(checklistItem, `[${customCheckboxContainerAttr}]`) : null;

    if (container && checklistItem && textSpan) {
      const selection = editor.selection.getSel();
      const range = selection?.getRangeAt(0);
      const cursorPosition = range?.startOffset || 0;
      const textContent = textSpan.textContent?.replace(/\u00A0/g, '').trim();

      if (cursorPosition === 0 && textContent === '') {
        e.preventDefault();
        doDeleteOnBackspace(container, checklistItem);
      }
    }
  }

  function doDeleteOnBackspace(container: HTMLElement, checklistItem: HTMLElement) {
    if (container.children.length === 1) {
      const newP = editor.dom.create('p', {}, '<br>');
      editor.dom.replace(newP, container);
      editor.selection.setCursorLocation(newP, 0);
    } else {
      const prevItem = checklistItem.previousElementSibling as HTMLElement;
      editor.dom.remove(checklistItem);

      if (prevItem) {
        const prevSpan = prevItem.querySelector(`[${customCheckboxTextAttr}]`);
        if (prevSpan?.firstChild) {
          const range = editor.dom.createRng();
          range.setStart(prevSpan.firstChild, prevSpan.textContent?.length || 0);
          range.setEnd(prevSpan.firstChild, prevSpan.textContent?.length || 0);
          editor.selection.setRng(range);
        }
      }
    }
  }
};

function injectStyleSheet(editor: any): void {
  const style = editor.getDoc().createElement('style');
  style.textContent = `${customCheckboxStyles}`;
  editor.getDoc().head.appendChild(style);
}
